import { Screen } from '../../components';
import { useState, useCallback, useContext } from 'react';
import './ProfilePage.scss';
import { AuthContext } from '../../contexts/AuthContext';
import { type IUser } from '../../types/user';
import { useUpdateUserProfile } from '../../services/user';
import { ProfileField } from './ProfileField';
import { IconEdit, IconCheck, IconX, IconUser, IconUpload, IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import { useUploadToS3 } from '../../hooks/useUploadToS3';
import { type Role } from '../../types/role';
import clsx from 'clsx';
import { Spin } from 'antd';

const ProfilePage = () => {
  const { user, setUser } = useContext(AuthContext);
  const [userEdit, setUserEdit] = useState<IUser>(user as IUser);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { updateUserProfile, loading } = useUpdateUserProfile();
  const { uploadFile } = useUploadToS3();

  const setEditing = useCallback(() => {
    setIsEditing(!isEditing);
  }, [isEditing]);

  const updateUserCallback = useCallback(async () => {
    setUser(userEdit);
    await updateUserProfile(userEdit);
    setIsEditing(false);
  }, [userEdit, setUser]);

  const setField = useCallback((key: string, value: string) => {
    setUserEdit((prev) => {
      return { ...prev, [key]: value };
    });
  }, []);

  const handleAvatarChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target?.files;
    if (files?.length) {
      const url = await uploadFile(files[0]);
      if (url) {
        setField('avatar', url);
      }
    }
  }, [uploadFile, setField]);

  const cancelEdit = useCallback(() => {
    setUserEdit(user as IUser);
    setIsEditing(false);
  }, [user]);

  return (
    <Screen id="profile" title="Mi perfil">
      <div className="w-full md:max-w-4xl -mx-8 sm:-mx-6 md:mx-auto">
        <div className={clsx(
          'border-2 transition-all duration-300 overflow-hidden',
          'bg-white border-gray-200 shadow-md',
          'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
          'hover:shadow-lg dark:hover:shadow-xl',
          // Mobile: full width, no rounded corners on sides, no borders on sides
          'rounded-none md:rounded-xl',
          // Mobile: extend to edges
          'border-x-0 md:border-x-2'
        )}>
          <div className="p-4 sm:p-6 md:p-8">
            {/* Header with Edit button */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-gray-200 dark:border-borders">
              <h2 className={clsx(
                'text-xl sm:text-2xl md:text-3xl font-bold transition-colors duration-300',
                'text-gray-900 dark:text-white'
              )}>
                Mis datos
              </h2>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                {isEditing ? (
                  <>
                    <button
                      onClick={cancelEdit}
                      disabled={loading}
                      className={clsx(
                        'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-300',
                        'bg-gray-100 text-gray-700 hover:bg-gray-200',
                        'dark:bg-surface-light dark:text-gray-300 dark:hover:bg-gray-600',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
                        'dark:focus:ring-offset-surface-dark',
                        'flex-1 sm:flex-initial'
                      )}
                    >
                      <IconX size={16} strokeWidth={2} className="sm:w-[18px] sm:h-[18px]" />
                      <span className="hidden sm:inline">Cancelar</span>
                      <span className="sm:hidden">Cancel</span>
                    </button>
                    <button
                      onClick={updateUserCallback}
                      disabled={loading}
                      className={clsx(
                        'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-300',
                        'bg-blue-600 text-white hover:bg-blue-700',
                        'dark:bg-blue-500 dark:hover:bg-blue-600',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                        'dark:focus:ring-offset-surface-dark',
                        'shadow-sm hover:shadow-md',
                        'flex-1 sm:flex-initial'
                      )}
                    >
                      {loading ? (
                        <Spin size="small" />
                      ) : (
                        <IconCheck size={16} strokeWidth={2} className="sm:w-[18px] sm:h-[18px]" />
                      )}
                      <span className="hidden sm:inline">Guardar</span>
                      <span className="sm:hidden">Guardar</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={setEditing}
                    className={clsx(
                      'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-300',
                      'bg-blue-600 text-white hover:bg-blue-700',
                      'dark:bg-blue-500 dark:hover:bg-blue-600',
                      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                      'dark:focus:ring-offset-surface-dark',
                      'shadow-sm hover:shadow-md',
                      'w-full sm:w-auto'
                    )}
                  >
                    <IconEdit size={16} strokeWidth={2} className="sm:w-[18px] sm:h-[18px]" />
                    Editar
                  </button>
                )}
              </div>
            </div>

            {/* Avatar Section */}
            <div className="flex flex-col items-center mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-200 dark:border-borders">
              <div className="relative mb-3 sm:mb-4">
                {userEdit.avatar ? (
                  <img
                    src={userEdit.avatar}
                    alt={userEdit.name || 'Avatar'}
                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-gray-200 dark:border-borders shadow-lg"
                  />
                ) : (
                  <div className={clsx(
                    'w-24 h-24 sm:w-32 sm:h-32 rounded-full flex items-center justify-center',
                    'bg-gradient-to-br from-blue-500 to-purple-500',
                    'dark:from-blue-600 dark:to-purple-600',
                    'border-4 border-gray-200 dark:border-borders shadow-lg'
                  )}>
                    <IconUser size={36} className="text-white sm:w-12 sm:h-12" />
                  </div>
                )}
                {isEditing && (
                  <label className={clsx(
                    'absolute bottom-0 right-0 p-2 sm:p-3 rounded-full cursor-pointer transition-all duration-300',
                    'bg-blue-600 text-white hover:bg-blue-700',
                    'dark:bg-blue-500 dark:hover:bg-blue-600',
                    'shadow-lg hover:shadow-xl',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                    'dark:focus:ring-offset-surface-dark'
                  )}>
                    <IconUpload size={16} strokeWidth={2} className="sm:w-5 sm:h-5" />
                    <input
                      type="file"
                      accept=".jpg,.png,.jpeg"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <h3 className={clsx(
                'text-lg sm:text-xl font-bold mb-1 transition-colors duration-300',
                'text-gray-900 dark:text-white text-center'
              )}>
                {userEdit.name || 'Sin nombre'}
              </h3>
              <p className={clsx(
                'text-xs sm:text-sm transition-colors duration-300 text-center',
                'text-gray-600 dark:text-gray-400'
              )}>
                {(userEdit?.role as Role)?.name || 'Usuario'}
              </p>
            </div>

            {/* Profile Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <ProfileField
                label="Nombre"
                value={userEdit.name || ''}
                fieldKey="name"
                isEditing={isEditing}
                setField={setField}
              />
              <ProfileField
                label="DNI"
                value={userEdit.DNI || ''}
                fieldKey="DNI"
                isEditing={isEditing}
                setField={setField}
              />
              <ProfileField
                label="Teléfono"
                value={userEdit.phone || ''}
                fieldKey="phone"
                isEditing={isEditing}
                setField={setField}
              />
              <ProfileField
                label="Email"
                value={userEdit.email || ''}
                fieldKey="email"
                isEditing={false}
                setField={setField}
                readOnly
              />
              <ProfileField
                label="Calle"
                value={userEdit.street || ''}
                fieldKey="street"
                isEditing={isEditing}
                setField={setField}
              />
              <div className={clsx(
                'p-4 rounded-lg border-2 transition-all duration-300',
                'bg-gray-50 border-gray-200',
                'dark:bg-surface-medium dark:border-borders'
              )}>
                <label className={clsx(
                  'block text-sm font-semibold mb-2 transition-colors duration-300',
                  'text-gray-700 dark:text-gray-300'
                )}>
                  Estado
                </label>
                <div className="flex items-center gap-2">
                  {userEdit?.active ? (
                    <IconCircleCheck size={16} strokeWidth={2} className="sm:w-[18px] sm:h-[18px]" />
                  ) : (
                    <IconCircleX size={16} strokeWidth={2} className="sm:w-[18px] sm:h-[18px]" />
                  )}
                  <span className={clsx(
                    'text-sm transition-colors duration-300',
                    'text-gray-700 dark:text-gray-300'
                  )}>
                    {userEdit?.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Screen>
  );
};

export { ProfilePage };
